import Phaser from 'phaser';
import { GAME, PLAYER, ASSETS } from '../config';
import { Player } from '../entities/player';
import { showTutorialStep } from '../ui/tutorial-overlay';
import { loadProgress, saveProgress } from '../../shared/progress';
import { BgmPlayer } from '../audio/bgm-player';

interface TutorialStep {
  key: 'move' | 'shoot' | 'skill' | 'done';
}

const STEPS: TutorialStep[] = [
  { key: 'move' },
  { key: 'shoot' },
  { key: 'skill' },
  { key: 'done' },
];

export class TutorialScene extends Phaser.Scene {
  private player!: Player;
  private playerSprite!: Phaser.GameObjects.Sprite;
  private cursorKeys!: Phaser.Types.Input.Keyboard.CursorKeys;
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private prevTouchSpace: boolean = false;
  private currentStep: number = 0;
  private currentOverlay: Phaser.GameObjects.Container | null = null;
  private music!: BgmPlayer;

  constructor() { super('TutorialScene'); }

  preload(): void {
    this.load.svg(ASSETS.playerTank, 'assets/tanks/player.svg', { width: 64, height: 64 });
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1a3344');

    const playerY = GAME.height * 0.85;
    this.player = new Player({ x: GAME.width / 2, y: playerY });
    this.playerSprite = this.add.sprite(this.player.x, playerY, ASSETS.playerTank);
    this.playerSprite.setDisplaySize(PLAYER.size, PLAYER.size);

    this.cursorKeys = this.input.keyboard!.createCursorKeys();
    this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    this.currentStep = 0;
    this.currentOverlay = null;
    this.advanceStep();

    this.music = new BgmPlayer();
    this.music.start();
    this.music.playTutorialBgm();

    this.events.on('shutdown', () => this.music.stop());
  }

  private advanceStep(): void {
    if (this.currentOverlay) {
      this.currentOverlay.destroy();
      this.currentOverlay = null;
    }

    const step = STEPS[this.currentStep];
    if (!step || step.key === 'done') {
      this.finish();
      return;
    }

    switch (step.key) {
      case 'move': {
        const name = loadProgress()?.studentName?.trim() || '';
        const greeting = name ? `👋 你好,${name} 指揮官!` : '👋 你好,指揮官!';
        this.currentOverlay = showTutorialStep(this, {
          targetX: this.player.x,
          targetY: this.player.y,
          dialogText: `${greeting}\n按 ← 跟 → 移動坦克`,
        });
        break;
      }

      case 'shoot':
        this.currentOverlay = showTutorialStep(this, {
          targetX: this.player.x,
          targetY: this.player.y - 50,
          dialogText: '⚡ 你的坦克會自動射擊。\n等砲彈飛出去後繼續...',
        });
        // auto-advance after 2 seconds
        this.time.delayedCall(2000, () => {
          this.currentStep++;
          this.advanceStep();
        });
        return;

      case 'skill':
        this.currentOverlay = showTutorialStep(this, {
          targetX: this.player.x,
          targetY: this.player.y,
          dialogText: '✨ 試試看按 空格 鍵放散彈技能!',
        });
        break;
    }
  }

  update(_t: number, deltaMs: number): void {
    const dtSec = deltaMs / 1000;
    const step = STEPS[this.currentStep];
    if (!step) return;

    // Movement handling
    // 2026-05-29 mobile-touch fix: synthetic KeyboardEvent unreliable on real
    // mobile (per horizontal-battle-scene.ts pattern). Touch HUD writes
    // window.__touchInput directly; OR with cursor state so desktop works.
    const touch = (window as unknown as { __touchInput?: { left: boolean; right: boolean; space: boolean } }).__touchInput;
    let dx = 0;
    if (this.cursorKeys.left.isDown || touch?.left) dx -= 1;
    if (this.cursorKeys.right.isDown || touch?.right) dx += 1;
    if (dx !== 0) {
      this.player.moveBy(dx * 200 * dtSec, { minX: 32, maxX: GAME.width - 32 });
      this.playerSprite.x = this.player.x;
      if (step.key === 'move') {
        this.currentStep++;
        this.advanceStep();
      }
    }

    // Skill trigger (space bar OR touch ⚡ button — edge detect on touch
    // because window.__touchInput.space stays true while held)
    const touchSpaceNow = !!touch?.space;
    const touchSpaceJustDown = touchSpaceNow && !this.prevTouchSpace;
    this.prevTouchSpace = touchSpaceNow;
    if (step.key === 'skill' && (Phaser.Input.Keyboard.JustDown(this.spaceKey) || touchSpaceJustDown)) {
      // Simple spread-shot visual: 3 circles fanning upward
      for (const angleDeg of [-15, 0, 15]) {
        const rad = (-90 + angleDeg) * (Math.PI / 180);
        const projectile = this.add.circle(this.player.x, this.player.y, 6, 0xffd54f);
        this.tweens.add({
          targets: projectile,
          x: this.player.x + Math.cos(rad) * 400,
          y: this.player.y + Math.sin(rad) * 400,
          alpha: 0,
          duration: 700,
          onComplete: () => projectile.destroy(),
        });
      }
      this.currentStep++;
      this.advanceStep();
    }
  }

  private finish(): void {
    const p = loadProgress();
    if (p) {
      p.tutorialDone = true;
      saveProgress(p);
    }
    this.add.text(GAME.width / 2, GAME.height / 2, '✅ 教學完成!', {
      fontSize: '48px',
      color: '#80cbc4',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.time.delayedCall(1500, () => {
      window.dispatchEvent(new CustomEvent('ef:return-home'));
    });
  }
}
